import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn
} from '@angular/forms';
import {Subject, takeUntil} from 'rxjs';
import {
  GeneratedAprOfferDraft,
  GeneratedFinanceOfferDraft,
  GeneratedLeaseOfferDraft,
  GeneratedOfferSetDraft,
  MultiVinVisibilityScope
} from '../models/generated-offer-set-draft.model';
import {
  QualifyingRuleSourceAprOffer,
  QualifyingRuleSourceDetail,
  QualifyingRuleSourceFinanceOffer,
  QualifyingRuleSourceLeaseOffer,
  QualifyingRuleSourceOffers,
  QualifyingRuleSourceVehicle
} from '../models/qualifying-rule-source-detail.model';
import {QualifyingRuleSourceRow} from '../models/qualifying-rule-source.model';
import {
  cloneGeneratedOfferSetDraft,
  cloneQualifyingRuleSource,
  mapSourceToGeneratedOfferSetDraft
} from '../utilities/offer-rule-workflow.mapper';

enum MoneyDownMode {
  Dollar = 'dollar',
  Percentage = 'percentage'
}

enum OfferSectionLabel {
  Lease = 'Lease',
  AlternateLease = 'Alternate Lease',
  Finance = 'Finance',
  PromotionalApr = 'Promotional APR'
}

enum OfferValidationKey {
  NonNegativeDecimal = 'nonNegativeDecimal',
  PositiveSafeInteger = 'positiveSafeInteger',
  NonNegativeSafeInteger = 'nonNegativeSafeInteger',
  Required = 'required'
}

interface ParseResult<T> {
  valid: boolean;
  value: T | null;
}

type NumericParseResult = ParseResult<number>;
type DecimalParseResult = ParseResult<string>;

interface LeaseFormControls {
  paymentAmount: FormControl<string>;
  moneyDown: FormControl<string>;
  percentageDown: FormControl<string>;
  term: FormControl<string>;
  annualMileage: FormControl<string>;
  additionalText: FormControl<string | null>;
  isCustom: FormControl<boolean>;
}

interface FinanceFormControls {
  paymentAmount: FormControl<string>;
  moneyDown: FormControl<string>;
  percentageDown: FormControl<string>;
  apr: FormControl<string>;
  term: FormControl<string>;
  additionalText: FormControl<string | null>;
  isCustom: FormControl<boolean>;
}

interface AprFormControls {
  apr: FormControl<string>;
  term: FormControl<string>;
  additionalText: FormControl<string | null>;
  useDefaultSettings: FormControl<boolean>;
}

interface OfferSetupFormControls {
  multiVinVisibility: FormControl<MultiVinVisibilityScope>;
  lease: FormGroup<LeaseFormControls>;
  alternateLease: FormGroup<LeaseFormControls>;
  finance: FormGroup<FinanceFormControls>;
  promotionalApr: FormGroup<AprFormControls>;
}

type NumericParser = (value: unknown) => NumericParseResult | DecimalParseResult;

const blankNumericInput = '';
const minimumSourceId = 1;
const nonNegativeDecimalPattern = /^(0|[1-9]\d*)(\.\d{1,2})?$/;
const positiveIntegerPattern = /^[1-9]\d*$/;
const nonNegativeIntegerPattern = /^(?:0|[1-9]\d*)$/;
const maximumCurrency = '99999999.99';
const maximumPercentage = '999.99';
const invalidStageMessage = 'Review the highlighted custom offer fields before staging.';
const customOfferRequiredMessage = 'Please configure at least one custom offer to continue.';

function normalizeNumericText(value: unknown): string
{
  let normalizedValue = blankNumericInput;
  if (typeof value === 'string') {
    normalizedValue = value.trim();
  }

  return normalizedValue;
}

function parseNonNegativeDecimal(value: unknown): DecimalParseResult
{
  const normalizedValue = normalizeNumericText(value);
  const result: DecimalParseResult = {valid: true, value: null};
  if (normalizedValue !== blankNumericInput) {
    const hasValidSyntax = nonNegativeDecimalPattern.test(normalizedValue);
    if (!hasValidSyntax) {
      result.valid = false;
    }
    if (result.valid) {
      result.value = normalizeDecimal(normalizedValue);
      result.valid = compareDecimal(result.value, maximumCurrency) <= 0;
    }
  }

  return result;
}

function parsePercentageDecimal(value: unknown): DecimalParseResult
{
  const result = parseNonNegativeDecimal(value);
  if (result.value !== null) {
    result.valid = result.valid && compareDecimal(result.value, maximumPercentage) <= 0;
  }

  return result;
}

function normalizeDecimal(value: string): string
{
  const parts = value.split('.');
  const decimalPart = (parts[1] ?? '').padEnd(2, '0');
  const normalized = `${parts[0]}.${decimalPart}`;

  return normalized;
}

function compareDecimal(left: string, right: string): number
{
  let comparison = left.length - right.length;
  if (comparison === 0) {
    comparison = left.localeCompare(right);
  }

  return comparison;
}

function parsePositiveSafeInteger(value: unknown): NumericParseResult
{
  const normalizedValue = normalizeNumericText(value);
  const result: NumericParseResult = {valid: true, value: null};
  if (normalizedValue !== blankNumericInput) {
    const numericValue = Number(normalizedValue);
    const hasValidSyntax = positiveIntegerPattern.test(normalizedValue);
    if (!hasValidSyntax || !Number.isSafeInteger(numericValue) || numericValue <= 0) {
      result.valid = false;
    }
    if (result.valid) {
      result.value = numericValue;
    }
  }

  return result;
}

function parseNonNegativeSafeInteger(value: unknown): NumericParseResult
{
  const normalizedValue = normalizeNumericText(value);
  const result: NumericParseResult = {valid: true, value: null};
  if (normalizedValue !== blankNumericInput) {
    const numericValue = Number(normalizedValue);
    const hasValidSyntax = nonNegativeIntegerPattern.test(normalizedValue);
    if (!hasValidSyntax || !Number.isSafeInteger(numericValue) || numericValue < 0) {
      result.valid = false;
    }
    if (result.valid) {
      result.value = numericValue;
    }
  }

  return result;
}

function createNumericValidator(parser: NumericParser, validationKey: OfferValidationKey): ValidatorFn
{
  const validator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const result = parser(control.value);
    let errors: ValidationErrors | null = null;
    if (!result.valid) {
      errors = {[validationKey]: true};
    }

    return errors;
  };

  return validator;
}

function createRequiredNumericValidator(parser: NumericParser): ValidatorFn
{
  const validator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const result = parser(control.value);
    let errors: ValidationErrors | null = null;
    if (result.valid && result.value === null) {
      errors = {[OfferValidationKey.Required]: true};
    }
    if (!result.valid) {
      errors = {[OfferValidationKey.NonNegativeDecimal]: true};
    }

    return errors;
  };

  return validator;
}

function createRequiredPositiveIntegerValidator(): ValidatorFn
{
  const validator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const result = parsePositiveSafeInteger(control.value);
    let errors: ValidationErrors | null = null;
    if (result.valid && result.value === null) {
      errors = {[OfferValidationKey.Required]: true};
    }
    if (!result.valid) {
      errors = {[OfferValidationKey.PositiveSafeInteger]: true};
    }

    return errors;
  };

  return validator;
}

function createRequiredNonNegativeIntegerValidator(): ValidatorFn
{
  const validator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const result = parseNonNegativeSafeInteger(control.value);
    let errors: ValidationErrors | null = null;
    if (result.valid && result.value === null) {
      errors = {[OfferValidationKey.Required]: true};
    }
    if (!result.valid) {
      errors = {[OfferValidationKey.NonNegativeSafeInteger]: true};
    }

    return errors;
  };

  return validator;
}

const optionalDecimalValidator = createNumericValidator(
  parseNonNegativeDecimal,
  OfferValidationKey.NonNegativeDecimal
);
const optionalPercentageValidator = createNumericValidator(
  parsePercentageDecimal,
  OfferValidationKey.NonNegativeDecimal
);
const optionalPositiveIntegerValidator = createNumericValidator(
  parsePositiveSafeInteger,
  OfferValidationKey.PositiveSafeInteger
);
const optionalNonNegativeIntegerValidator = createNumericValidator(
  parseNonNegativeSafeInteger,
  OfferValidationKey.NonNegativeSafeInteger
);
const requiredDecimalValidator = createRequiredNumericValidator(parseNonNegativeDecimal);
const requiredPercentageValidator = createRequiredNumericValidator(parsePercentageDecimal);
const requiredPositiveIntegerValidator = createRequiredPositiveIntegerValidator();
const requiredNonNegativeIntegerValidator = createRequiredNonNegativeIntegerValidator();

@Component({
  selector: 'auto-ipop-rule-workflow-offer-setup',
  templateUrl: './ipop-rule-workflow-offer-setup.component.html',
  styleUrls: ['./ipop-rule-workflow-offer-setup.component.scss'],
  standalone: false
})
export class IpopRuleWorkflowOfferSetupComponent implements OnInit, OnChanges, OnDestroy
{
  @Input() selectedSource: QualifyingRuleSourceRow | null = null;
  @Input() sourceDetail: QualifyingRuleSourceDetail | null = null;
  @Input() existingGeneratedDraft: GeneratedOfferSetDraft | null = null;

  @Output() generatedOfferSetDraftChange = new EventEmitter<GeneratedOfferSetDraft | null>();
  @Output() readinessChange = new EventEmitter<boolean>();
  @Output() backRequested = new EventEmitter<void>();
  @Output() stageRequested = new EventEmitter<GeneratedOfferSetDraft>();

  readonly moneyDownModes = MoneyDownMode;
  readonly sectionLabels = OfferSectionLabel;
  readonly validationKeys = OfferValidationKey;
  readonly visibilityScopes = MultiVinVisibilityScope;
  readonly offerSetupForm = this.buildOfferSetupForm();

  selectedSourceSummary: QualifyingRuleSourceRow | null = null;
  leaseMoneyDownMode = MoneyDownMode.Dollar;
  alternateLeaseMoneyDownMode = MoneyDownMode.Dollar;
  financeMoneyDownMode = MoneyDownMode.Dollar;
  initializationMessage = 'Select a valid source and load its offer detail to configure offers.';
  readinessMessage = 'Offer Setup is not ready to stage.';
  stageErrorMessage = '';
  stageAttempted = false;

  private readonly destroySubject = new Subject<void>();
  private observedSourceId: number | null = null;
  private currentDraft: GeneratedOfferSetDraft | null = null;
  private lastReadiness: boolean | null = null;
  private inputsApplied = false;
  private formInitialized = false;
  private leaseNormalizeMoneyDown = false;
  private alternateLeaseNormalizeMoneyDown = false;
  private financeNormalizeMoneyDown = false;

  /** Subscribes to editable form changes after copied inputs are initialized. */
  ngOnInit(): void
  {
    if (!this.inputsApplied) {
      this.applyInputState();
    }
    this.offerSetupForm.valueChanges
      .pipe(takeUntil(this.destroySubject))
      .subscribe(() => this.emitCurrentState());
  }

  /** Applies changed parent inputs without mutating or repeatedly polling them. */
  ngOnChanges(changes: SimpleChanges): void
  {
    if (this.hasInitializationInputChange(changes)) {
      this.applyInputState();
    }
  }

  /** Releases the component form subscription. */
  ngOnDestroy(): void
  {
    this.destroySubject.next();
    this.destroySubject.complete();
  }

  /** Enables or disables Lease customization while preserving its values. */
  onLeaseCustomToggle(): void
  {
    const isCustom = !this.offerSetupForm.controls.lease.controls.isCustom.value;
    this.offerSetupForm.controls.lease.controls.isCustom.setValue(isCustom, {emitEvent: false});
    this.leaseNormalizeMoneyDown = isCustom;
    this.configureLeaseSection(this.offerSetupForm.controls.lease, isCustom, this.leaseMoneyDownMode);
    this.completeSectionTransition();
  }

  /** Enables or disables Alternate Lease customization while preserving its values. */
  onAlternateLeaseCustomToggle(): void
  {
    const isCustom = !this.offerSetupForm.controls.alternateLease.controls.isCustom.value;
    this.offerSetupForm.controls.alternateLease.controls.isCustom.setValue(isCustom, {emitEvent: false});
    this.alternateLeaseNormalizeMoneyDown = isCustom;
    this.configureLeaseSection(
      this.offerSetupForm.controls.alternateLease,
      isCustom,
      this.alternateLeaseMoneyDownMode
    );
    this.completeSectionTransition();
  }

  /** Enables or disables Finance customization while preserving its values. */
  onFinanceCustomToggle(): void
  {
    const isCustom = !this.offerSetupForm.controls.finance.controls.isCustom.value;
    this.offerSetupForm.controls.finance.controls.isCustom.setValue(isCustom, {emitEvent: false});
    this.financeNormalizeMoneyDown = isCustom;
    this.configureFinanceSection(isCustom);
    this.completeSectionTransition();
  }

  /** Inverts the APR customization control into use-default-settings semantics. */
  onPromotionalAprCustomToggle(): void
  {
    const useDefaultSettings = !this.offerSetupForm.controls.promotionalApr.controls.useDefaultSettings.value;
    this.offerSetupForm.controls.promotionalApr.controls.useDefaultSettings.setValue(
      useDefaultSettings,
      {emitEvent: false}
    );
    this.configureAprSection(!useDefaultSettings);
    this.completeSectionTransition();
  }

  /** Selects Lease dollar mode and clears the inactive percentage value. */
  onLeaseDollarMode(focusTarget: HTMLInputElement): void
  {
    this.leaseMoneyDownMode = MoneyDownMode.Dollar;
    this.leaseNormalizeMoneyDown = true;
    this.clearAndApplyLeaseMode(
      this.offerSetupForm.controls.lease,
      MoneyDownMode.Dollar,
      focusTarget
    );
  }

  /** Selects Lease percentage mode and clears the inactive dollar value. */
  onLeasePercentageMode(focusTarget: HTMLInputElement): void
  {
    this.leaseMoneyDownMode = MoneyDownMode.Percentage;
    this.leaseNormalizeMoneyDown = true;
    this.clearAndApplyLeaseMode(
      this.offerSetupForm.controls.lease,
      MoneyDownMode.Percentage,
      focusTarget
    );
  }

  /** Selects Alternate Lease dollar mode and clears the inactive percentage value. */
  onAlternateLeaseDollarMode(focusTarget: HTMLInputElement): void
  {
    this.alternateLeaseMoneyDownMode = MoneyDownMode.Dollar;
    this.alternateLeaseNormalizeMoneyDown = true;
    this.clearAndApplyLeaseMode(
      this.offerSetupForm.controls.alternateLease,
      MoneyDownMode.Dollar,
      focusTarget
    );
  }

  /** Selects Alternate Lease percentage mode and clears the inactive dollar value. */
  onAlternateLeasePercentageMode(focusTarget: HTMLInputElement): void
  {
    this.alternateLeaseMoneyDownMode = MoneyDownMode.Percentage;
    this.alternateLeaseNormalizeMoneyDown = true;
    this.clearAndApplyLeaseMode(
      this.offerSetupForm.controls.alternateLease,
      MoneyDownMode.Percentage,
      focusTarget
    );
  }

  /** Selects Finance dollar mode and clears the inactive percentage value. */
  onFinanceDollarMode(focusTarget: HTMLInputElement): void
  {
    this.financeMoneyDownMode = MoneyDownMode.Dollar;
    this.financeNormalizeMoneyDown = true;
    this.clearAndApplyFinanceMode(MoneyDownMode.Dollar, focusTarget);
  }

  /** Selects Finance percentage mode and clears the inactive dollar value. */
  onFinancePercentageMode(focusTarget: HTMLInputElement): void
  {
    this.financeMoneyDownMode = MoneyDownMode.Percentage;
    this.financeNormalizeMoneyDown = true;
    this.clearAndApplyFinanceMode(MoneyDownMode.Percentage, focusTarget);
  }

  /** Emits a navigation request without changing parent-owned state. */
  onBack(): void
  {
    this.backRequested.emit();
  }

  /** Emits a copied generated draft only when current state is ready. */
  onStage(): void
  {
    this.stageAttempted = true;
    this.stageErrorMessage = '';
    const isReady = this.calculateReadiness(this.currentDraft);
    if (!isReady) {
      this.offerSetupForm.markAllAsTouched();
      this.stageErrorMessage = invalidStageMessage;
      if (!this.hasCustomOfferConfigured()) {
        this.stageErrorMessage = customOfferRequiredMessage;
      }
      this.emitCurrentState();
    }
    if (isReady && this.currentDraft !== null) {
      const stagedDraft = cloneGeneratedOfferSetDraft(this.currentDraft);
      this.stageRequested.emit(stagedDraft);
    }
  }

  /** Reports whether a control error should be visible to the user. */
  shouldShowError(control: AbstractControl): boolean
  {
    const shouldShow = control.invalid && (control.touched || this.stageAttempted);

    return shouldShow;
  }

  /** Reports whether the current normalized draft can be staged. */
  isReady(): boolean
  {
    const ready = this.calculateReadiness(this.currentDraft);

    return ready;
  }

  /** Reports whether copied source or same-session state initialized the form. */
  isInitialized(): boolean
  {
    const initialized = this.formInitialized;

    return initialized;
  }

  /** Reports whether any offer section is configured as custom. */
  hasCustomOfferConfigured(): boolean
  {
    const draft = this.currentDraft;
    let configured = false;
    if (draft !== null) {
      configured = draft.lease.isCustom
        || draft.alternateLease.isCustom
        || draft.finance.isCustom
        || !draft.promotionalApr.useDefaultSettings;
    }

    return configured;
  }

  /** Creates the typed nested Offer Setup form. */
  private buildOfferSetupForm(): FormGroup<OfferSetupFormControls>
  {
    const form = new FormGroup<OfferSetupFormControls>({
      multiVinVisibility: new FormControl(MultiVinVisibilityScope.FullVisibility, {nonNullable: true}),
      lease: this.buildLeaseFormGroup(),
      alternateLease: this.buildLeaseFormGroup(),
      finance: this.buildFinanceFormGroup(),
      promotionalApr: this.buildAprFormGroup()
    });

    return form;
  }

  /** Creates a typed Lease or Alternate Lease form group. */
  private buildLeaseFormGroup(): FormGroup<LeaseFormControls>
  {
    const group = new FormGroup<LeaseFormControls>({
      paymentAmount: new FormControl(blankNumericInput, {nonNullable: true}),
      moneyDown: new FormControl(blankNumericInput, {nonNullable: true}),
      percentageDown: new FormControl(blankNumericInput, {nonNullable: true}),
      term: new FormControl(blankNumericInput, {nonNullable: true}),
      annualMileage: new FormControl(blankNumericInput, {nonNullable: true}),
      additionalText: new FormControl<string | null>(null),
      isCustom: new FormControl(false, {nonNullable: true})
    });

    return group;
  }

  /** Creates the typed Finance form group. */
  private buildFinanceFormGroup(): FormGroup<FinanceFormControls>
  {
    const group = new FormGroup<FinanceFormControls>({
      paymentAmount: new FormControl(blankNumericInput, {nonNullable: true}),
      moneyDown: new FormControl(blankNumericInput, {nonNullable: true}),
      percentageDown: new FormControl(blankNumericInput, {nonNullable: true}),
      apr: new FormControl(blankNumericInput, {nonNullable: true}),
      term: new FormControl(blankNumericInput, {nonNullable: true}),
      additionalText: new FormControl<string | null>(null),
      isCustom: new FormControl(false, {nonNullable: true})
    });

    return group;
  }

  /** Creates the typed Promotional APR form group. */
  private buildAprFormGroup(): FormGroup<AprFormControls>
  {
    const group = new FormGroup<AprFormControls>({
      apr: new FormControl(blankNumericInput, {nonNullable: true}),
      term: new FormControl(blankNumericInput, {nonNullable: true}),
      additionalText: new FormControl<string | null>(null),
      useDefaultSettings: new FormControl(true, {nonNullable: true})
    });

    return group;
  }

  /** Resolves input precedence and initializes only from valid identities. */
  private applyInputState(): void
  {
    this.inputsApplied = true;
    const nextSourceId = this.resolveSelectedSourceId();
    if (nextSourceId !== this.observedSourceId) {
      this.clearLocalState();
      this.observedSourceId = nextSourceId;
    }
    this.selectedSourceSummary = this.copyValidSelectedSource();
    const sourceDetailCopy = this.copyValidSourceDetail(nextSourceId);
    this.initializeFromAvailableInput(sourceDetailCopy, nextSourceId);
  }

  /** Uses an existing draft before mapped source detail. */
  private initializeFromAvailableInput(
    sourceDetailCopy: QualifyingRuleSourceDetail | null,
    selectedSourceId: number | null
  ): void
  {
    const hasValidIdentity = selectedSourceId !== null && this.selectedSourceSummary !== null;
    const hasIdentityConflict = this.hasSourceDetailConflict(selectedSourceId);
    const hasExistingDraft = this.existingGeneratedDraft !== null;
    if (hasExistingDraft && !hasIdentityConflict && this.existingGeneratedDraft !== null) {
      const draftCopy = cloneGeneratedOfferSetDraft(this.existingGeneratedDraft);
      this.initializeForm(draftCopy, true);
    }
    if (!hasExistingDraft && hasValidIdentity && !hasIdentityConflict && sourceDetailCopy !== null) {
      const mappedDraft = mapSourceToGeneratedOfferSetDraft(sourceDetailCopy);
      this.initializeForm(mappedDraft, false);
    }
    if ((!hasExistingDraft && !hasValidIdentity) || hasIdentityConflict || (!hasExistingDraft && sourceDetailCopy === null)) {
      this.clearOfferForm();
      this.initializationMessage = this.resolveInitializationMessage(hasIdentityConflict);
      this.emitCurrentState();
    }
  }

  /** Populates raw controls and applies semantic enabled states. */
  private initializeForm(draft: GeneratedOfferSetDraft, preserveExactDraft: boolean): void
  {
    this.offerSetupForm.controls.multiVinVisibility.setValue(draft.multiVinVisibility, {emitEvent: false});
    this.populateLeaseGroup(this.offerSetupForm.controls.lease, draft.lease);
    this.populateLeaseGroup(this.offerSetupForm.controls.alternateLease, draft.alternateLease);
    this.populateFinanceGroup(draft.finance);
    this.populateAprGroup(draft.promotionalApr);
    this.initializeMoneyDownModes(draft);
    this.configureAllSections();
    this.setMoneyDownNormalization(preserveExactDraft);
    this.formInitialized = true;
    this.stageAttempted = false;
    this.stageErrorMessage = '';
    this.initializationMessage = '';
    this.emitCurrentState();
  }

  /** Populates a Lease group without exposing its source object. */
  private populateLeaseGroup(group: FormGroup<LeaseFormControls>, draft: GeneratedLeaseOfferDraft): void
  {
    group.reset({
      paymentAmount: this.numberToInput(draft.paymentAmount),
      moneyDown: this.numberToInput(draft.moneyDown),
      percentageDown: this.numberToInput(draft.percentageDown),
      term: this.numberToInput(draft.term),
      annualMileage: this.numberToInput(draft.annualMileage),
      additionalText: draft.additionalText,
      isCustom: draft.isCustom
    }, {emitEvent: false});
  }

  /** Populates Finance controls without mutating the copied draft. */
  private populateFinanceGroup(draft: GeneratedFinanceOfferDraft): void
  {
    this.offerSetupForm.controls.finance.reset({
      paymentAmount: this.numberToInput(draft.paymentAmount),
      moneyDown: this.numberToInput(draft.moneyDown),
      percentageDown: this.numberToInput(draft.percentageDown),
      apr: this.numberToInput(draft.apr),
      term: this.numberToInput(draft.term),
      additionalText: draft.additionalText,
      isCustom: draft.isCustom
    }, {emitEvent: false});
  }

  /** Populates Promotional APR controls without mutating the copied draft. */
  private populateAprGroup(draft: GeneratedAprOfferDraft): void
  {
    this.offerSetupForm.controls.promotionalApr.reset({
      apr: this.numberToInput(draft.apr),
      term: this.numberToInput(draft.term),
      additionalText: draft.additionalText,
      useDefaultSettings: draft.useDefaultSettings
    }, {emitEvent: false});
  }

  /** Derives local money-down modes using legacy precedence. */
  private initializeMoneyDownModes(draft: GeneratedOfferSetDraft): void
  {
    this.leaseMoneyDownMode = this.resolveMoneyDownMode(draft.lease);
    this.alternateLeaseMoneyDownMode = this.resolveMoneyDownMode(draft.alternateLease);
    this.financeMoneyDownMode = this.resolveMoneyDownMode(draft.finance);
  }

  /** Applies enabled state and validators to all initialized sections. */
  private configureAllSections(): void
  {
    const lease = this.offerSetupForm.controls.lease;
    const alternateLease = this.offerSetupForm.controls.alternateLease;
    this.configureLeaseSection(lease, lease.controls.isCustom.value, this.leaseMoneyDownMode);
    this.configureLeaseSection(
      alternateLease,
      alternateLease.controls.isCustom.value,
      this.alternateLeaseMoneyDownMode
    );
    this.configureFinanceSection(this.offerSetupForm.controls.finance.controls.isCustom.value);
    const useDefaults = this.offerSetupForm.controls.promotionalApr.controls.useDefaultSettings.value;
    this.configureAprSection(!useDefaults);
  }

  /** Applies optional Lease validators only while customization is active. */
  private configureLeaseSection(
    group: FormGroup<LeaseFormControls>,
    isCustom: boolean,
    mode: MoneyDownMode
  ): void
  {
    this.setLeaseValidators(group, isCustom, mode);
    this.setLeaseControlState(group, isCustom);
    if (isCustom) {
      this.applyLeaseMoneyDownControlState(group, mode);
    }
    group.updateValueAndValidity({emitEvent: false});
  }

  /** Adds or removes all Lease custom validators. */
  private setLeaseValidators(
    group: FormGroup<LeaseFormControls>,
    isCustom: boolean,
    mode: MoneyDownMode
  ): void
  {
    const useDollars = mode === MoneyDownMode.Dollar;
    this.setControlValidator(group.controls.paymentAmount, requiredDecimalValidator, isCustom);
    this.setControlValidator(group.controls.moneyDown, requiredDecimalValidator, isCustom && useDollars);
    this.setControlValidator(group.controls.percentageDown, requiredPercentageValidator, isCustom && !useDollars);
    this.setControlValidator(group.controls.term, requiredPositiveIntegerValidator, isCustom);
    this.setControlValidator(group.controls.annualMileage, requiredNonNegativeIntegerValidator, isCustom);
  }

  /** Enables editable Lease values or disables them as preserved defaults. */
  private setLeaseControlState(group: FormGroup<LeaseFormControls>, isCustom: boolean): void
  {
    this.setControlEnabled(group.controls.paymentAmount, isCustom);
    this.setControlEnabled(group.controls.moneyDown, isCustom);
    this.setControlEnabled(group.controls.percentageDown, isCustom);
    this.setControlEnabled(group.controls.term, isCustom);
    this.setControlEnabled(group.controls.annualMileage, isCustom);
    this.setControlEnabled(group.controls.additionalText, isCustom);
  }

  /** Applies optional Finance validators only while customization is active. */
  private configureFinanceSection(isCustom: boolean): void
  {
    const group = this.offerSetupForm.controls.finance;
    this.setFinanceValidators(group, isCustom);
    this.setFinanceControlState(group, isCustom);
    if (isCustom) {
      this.applyFinanceMoneyDownControlState(this.financeMoneyDownMode);
    }
    group.updateValueAndValidity({emitEvent: false});
  }

  /** Adds or removes all Finance custom validators. */
  private setFinanceValidators(group: FormGroup<FinanceFormControls>, isCustom: boolean): void
  {
    const useDollars = this.financeMoneyDownMode === MoneyDownMode.Dollar;
    this.setControlValidator(group.controls.paymentAmount, requiredDecimalValidator, isCustom);
    this.setControlValidator(group.controls.moneyDown, requiredDecimalValidator, isCustom && useDollars);
    this.setControlValidator(group.controls.percentageDown, requiredPercentageValidator, isCustom && !useDollars);
    this.setControlValidator(group.controls.apr, requiredPercentageValidator, isCustom);
    this.setControlValidator(group.controls.term, requiredPositiveIntegerValidator, isCustom);
  }

  /** Enables editable Finance values or disables them as preserved defaults. */
  private setFinanceControlState(group: FormGroup<FinanceFormControls>, isCustom: boolean): void
  {
    this.setControlEnabled(group.controls.paymentAmount, isCustom);
    this.setControlEnabled(group.controls.moneyDown, isCustom);
    this.setControlEnabled(group.controls.percentageDown, isCustom);
    this.setControlEnabled(group.controls.apr, isCustom);
    this.setControlEnabled(group.controls.term, isCustom);
    this.setControlEnabled(group.controls.additionalText, isCustom);
  }

  /** Applies required APR validators only while custom settings are active. */
  private configureAprSection(isCustom: boolean): void
  {
    const group = this.offerSetupForm.controls.promotionalApr;
    this.setControlValidator(group.controls.apr, requiredPercentageValidator, isCustom);
    this.setControlValidator(group.controls.term, requiredPositiveIntegerValidator, isCustom);
    this.setControlEnabled(group.controls.apr, isCustom);
    this.setControlEnabled(group.controls.term, isCustom);
    this.setControlEnabled(group.controls.additionalText, isCustom);
    group.updateValueAndValidity({emitEvent: false});
  }

  /** Assigns one validator or clears custom validation. */
  private setControlValidator(control: AbstractControl, validator: ValidatorFn, active: boolean): void
  {
    control.clearValidators();
    if (active) {
      control.setValidators([validator]);
    }
    control.updateValueAndValidity({emitEvent: false});
  }

  /** Enables or disables a value control without emitting transient state. */
  private setControlEnabled(control: AbstractControl, enabled: boolean): void
  {
    if (enabled) {
      control.enable({emitEvent: false});
    }
    if (!enabled) {
      control.disable({emitEvent: false});
    }
  }

  /** Keeps only the active Lease money-down control enabled while custom. */
  private applyLeaseMoneyDownControlState(group: FormGroup<LeaseFormControls>, mode: MoneyDownMode): void
  {
    const useDollars = mode === MoneyDownMode.Dollar;
    this.setControlEnabled(group.controls.moneyDown, useDollars);
    this.setControlEnabled(group.controls.percentageDown, !useDollars);
  }

  /** Keeps only the active Finance money-down control enabled while custom. */
  private applyFinanceMoneyDownControlState(mode: MoneyDownMode): void
  {
    const group = this.offerSetupForm.controls.finance;
    const useDollars = mode === MoneyDownMode.Dollar;
    this.setControlEnabled(group.controls.moneyDown, useDollars);
    this.setControlEnabled(group.controls.percentageDown, !useDollars);
  }

  /** Clears a Lease inactive value, reapplies mode state, and moves user-requested focus. */
  private clearAndApplyLeaseMode(
    group: FormGroup<LeaseFormControls>,
    mode: MoneyDownMode,
    focusTarget: HTMLInputElement
  ): void
  {
    this.clearInactiveLeaseMoneyDown(group, mode);
    this.applyLeaseMoneyDownControlState(group, mode);
    this.completeModeTransition(focusTarget);
  }

  /** Clears the Finance inactive value and moves user-requested focus. */
  private clearAndApplyFinanceMode(mode: MoneyDownMode, focusTarget: HTMLInputElement): void
  {
    const group = this.offerSetupForm.controls.finance;
    this.clearInactiveFinanceMoneyDown(group, mode);
    this.applyFinanceMoneyDownControlState(mode);
    this.completeModeTransition(focusTarget);
  }

  /** Clears only the inactive Lease money-down raw value. */
  private clearInactiveLeaseMoneyDown(group: FormGroup<LeaseFormControls>, mode: MoneyDownMode): void
  {
    if (mode === MoneyDownMode.Dollar) {
      group.controls.percentageDown.setValue(blankNumericInput, {emitEvent: false});
    }
    if (mode === MoneyDownMode.Percentage) {
      group.controls.moneyDown.setValue(blankNumericInput, {emitEvent: false});
    }
  }

  /** Clears only the inactive Finance money-down raw value. */
  private clearInactiveFinanceMoneyDown(group: FormGroup<FinanceFormControls>, mode: MoneyDownMode): void
  {
    if (mode === MoneyDownMode.Dollar) {
      group.controls.percentageDown.setValue(blankNumericInput, {emitEvent: false});
    }
    if (mode === MoneyDownMode.Percentage) {
      group.controls.moneyDown.setValue(blankNumericInput, {emitEvent: false});
    }
  }

  /** Completes a toggle transition and refreshes the typed outputs. */
  private completeSectionTransition(): void
  {
    this.stageErrorMessage = '';
    this.emitCurrentState();
  }

  /** Completes a user-originated mode transition and focuses its active input. */
  private completeModeTransition(focusTarget: HTMLInputElement): void
  {
    this.stageErrorMessage = '';
    this.emitCurrentState();
    queueMicrotask(() => focusTarget.focus());
  }

  /** Emits current typed draft and readiness from raw controls. */
  private emitCurrentState(): void
  {
    const draft = this.buildGeneratedDraft();
    this.currentDraft = draft;
    let emittedDraft: GeneratedOfferSetDraft | null = null;
    if (draft !== null) {
      emittedDraft = cloneGeneratedOfferSetDraft(draft);
    }
    this.generatedOfferSetDraftChange.emit(emittedDraft);
    this.emitReadiness(this.calculateReadiness(draft));
  }

  /** Emits readiness only when its summary state changes. */
  private emitReadiness(readiness: boolean): void
  {
    this.readinessMessage = customOfferRequiredMessage;
    if (readiness) {
      this.readinessMessage = 'Offer Setup is ready to stage.';
    }
    if (!readiness && this.hasCustomOfferConfigured()) {
      this.readinessMessage = 'Offer Setup is not ready to stage.';
    }
    if (readiness !== this.lastReadiness) {
      this.lastReadiness = readiness;
      this.readinessChange.emit(readiness);
    }
  }

  /** Builds one allow-listed generated draft when all raw values are parseable. */
  private buildGeneratedDraft(): GeneratedOfferSetDraft | null
  {
    let generatedDraft: GeneratedOfferSetDraft | null = null;
    if (this.formInitialized && this.hasOfferSetupIdentity()) {
      const lease = this.buildLeaseDraft(
        this.offerSetupForm.controls.lease,
        this.leaseMoneyDownMode,
        this.leaseNormalizeMoneyDown
      );
      const alternateLease = this.buildLeaseDraft(
        this.offerSetupForm.controls.alternateLease,
        this.alternateLeaseMoneyDownMode,
        this.alternateLeaseNormalizeMoneyDown
      );
      const finance = this.buildFinanceDraft();
      const promotionalApr = this.buildAprDraft();
      if (lease !== null && alternateLease !== null && finance !== null && promotionalApr !== null) {
        generatedDraft = {
          multiVinVisibility: this.offerSetupForm.controls.multiVinVisibility.value,
          lease,
          alternateLease,
          finance,
          promotionalApr
        };
      }
    }

    return generatedDraft;
  }

  /** Maps one Lease group to finite typed values or rejects malformed input. */
  private buildLeaseDraft(
    group: FormGroup<LeaseFormControls>,
    mode: MoneyDownMode,
    normalizeMoneyDown: boolean
  ): GeneratedLeaseOfferDraft | null
  {
    const payment = parseNonNegativeDecimal(group.controls.paymentAmount.value);
    const moneyDown = parseNonNegativeDecimal(group.controls.moneyDown.value);
    const percentageDown = parsePercentageDecimal(group.controls.percentageDown.value);
    const term = parsePositiveSafeInteger(group.controls.term.value);
    const annualMileage = parseNonNegativeSafeInteger(group.controls.annualMileage.value);
    let draft: GeneratedLeaseOfferDraft | null = null;
    if (payment.valid && moneyDown.valid && percentageDown.valid && term.valid && annualMileage.valid) {
      draft = {
        paymentAmount: payment.value,
        moneyDown: moneyDown.value,
        percentageDown: percentageDown.value,
        term: term.value,
        annualMileage: annualMileage.value,
        additionalText: group.controls.additionalText.value,
        isCustom: group.controls.isCustom.value
      };
      this.normalizeLeaseMoneyDown(draft, mode, normalizeMoneyDown);
    }

    return draft;
  }

  /** Maps Finance controls to finite typed values or rejects malformed input. */
  private buildFinanceDraft(): GeneratedFinanceOfferDraft | null
  {
    const group = this.offerSetupForm.controls.finance;
    const payment = parseNonNegativeDecimal(group.controls.paymentAmount.value);
    const moneyDown = parseNonNegativeDecimal(group.controls.moneyDown.value);
    const percentageDown = parsePercentageDecimal(group.controls.percentageDown.value);
    const apr = parsePercentageDecimal(group.controls.apr.value);
    const term = parsePositiveSafeInteger(group.controls.term.value);
    let draft: GeneratedFinanceOfferDraft | null = null;
    if (payment.valid && moneyDown.valid && percentageDown.valid && apr.valid && term.valid) {
      draft = this.createFinanceDraft(group, payment, moneyDown, percentageDown, apr, term);
      this.normalizeFinanceMoneyDown(draft);
    }

    return draft;
  }

  /** Creates the allow-listed Finance draft after successful parsing. */
  private createFinanceDraft(
    group: FormGroup<FinanceFormControls>,
    payment: DecimalParseResult,
    moneyDown: DecimalParseResult,
    percentageDown: DecimalParseResult,
    apr: DecimalParseResult,
    term: NumericParseResult
  ): GeneratedFinanceOfferDraft
  {
    const draft: GeneratedFinanceOfferDraft = {
      paymentAmount: payment.value,
      moneyDown: moneyDown.value,
      percentageDown: percentageDown.value,
      apr: apr.value,
      term: term.value,
      additionalText: group.controls.additionalText.value,
      isCustom: group.controls.isCustom.value
    };

    return draft;
  }

  /** Maps Promotional APR controls to finite typed values or rejects malformed input. */
  private buildAprDraft(): GeneratedAprOfferDraft | null
  {
    const group = this.offerSetupForm.controls.promotionalApr;
    const apr = parseNonNegativeDecimal(group.controls.apr.value);
    const term = parsePositiveSafeInteger(group.controls.term.value);
    let draft: GeneratedAprOfferDraft | null = null;
    if (apr.valid && term.valid) {
      draft = {
        apr: apr.value,
        term: term.value,
        additionalText: group.controls.additionalText.value,
        useDefaultSettings: group.controls.useDefaultSettings.value
      };
    }

    return draft;
  }

  /** Clears an inactive Lease value according to the selected down-payment mode. */
  private normalizeLeaseMoneyDown(
    draft: GeneratedLeaseOfferDraft,
    mode: MoneyDownMode,
    normalizeMoneyDown: boolean
  ): void
  {
    if (normalizeMoneyDown && mode === MoneyDownMode.Dollar) {
      draft.percentageDown = null;
    }
    if (normalizeMoneyDown && mode === MoneyDownMode.Percentage) {
      draft.moneyDown = null;
    }
  }

  /** Clears an inactive Finance value according to the selected down-payment mode. */
  private normalizeFinanceMoneyDown(draft: GeneratedFinanceOfferDraft): void
  {
    if (this.financeNormalizeMoneyDown && this.financeMoneyDownMode === MoneyDownMode.Dollar) {
      draft.percentageDown = null;
    }
    if (this.financeNormalizeMoneyDown && this.financeMoneyDownMode === MoneyDownMode.Percentage) {
      draft.moneyDown = null;
    }
  }

  /** Calculates readiness from identity, initialization, parsing, and active validation. */
  private calculateReadiness(draft: GeneratedOfferSetDraft | null): boolean
  {
    const readiness = this.formInitialized
      && this.hasOfferSetupIdentity()
      && draft !== null
      && this.hasCustomOfferConfigured()
      && this.offerSetupForm.valid;

    return readiness;
  }

  /** Accepts create source identity or an authoritative persisted draft. */
  private hasOfferSetupIdentity(): boolean
  {
    const hasIdentity = this.selectedSourceSummary !== null || this.existingGeneratedDraft !== null;

    return hasIdentity;
  }

  /** Resets stale state before applying a replacement source identity. */
  private clearLocalState(): void
  {
    this.selectedSourceSummary = null;
    this.clearOfferForm();
    this.generatedOfferSetDraftChange.emit(null);
    this.emitReadiness(false);
  }

  /** Clears every offer control and disables all default sections. */
  private clearOfferForm(): void
  {
    const blankDetail = this.createBlankSourceDetail();
    const blankDraft = mapSourceToGeneratedOfferSetDraft(blankDetail);
    this.populateLeaseGroup(this.offerSetupForm.controls.lease, blankDraft.lease);
    this.populateLeaseGroup(this.offerSetupForm.controls.alternateLease, blankDraft.alternateLease);
    this.populateFinanceGroup(blankDraft.finance);
    this.populateAprGroup(blankDraft.promotionalApr);
    this.initializeMoneyDownModes(blankDraft);
    this.configureAllSections();
    this.formInitialized = false;
    this.currentDraft = null;
    this.setMoneyDownNormalization(false);
    this.stageAttempted = false;
    this.stageErrorMessage = '';
  }

  /** Tracks whether money-down output should clear its inactive value. */
  private setMoneyDownNormalization(preserveExactDraft: boolean): void
  {
    this.leaseNormalizeMoneyDown = !preserveExactDraft;
    this.alternateLeaseNormalizeMoneyDown = !preserveExactDraft;
    this.financeNormalizeMoneyDown = !preserveExactDraft;
  }

  /** Resolves local mode from meaningful percentage-only source state. */
  private resolveMoneyDownMode(
    draft: GeneratedLeaseOfferDraft | GeneratedFinanceOfferDraft
  ): MoneyDownMode
  {
    let mode = MoneyDownMode.Dollar;
    const hasMeaningfulPercentage = draft.percentageDown !== null && Number(draft.percentageDown) > 0;
    if (hasMeaningfulPercentage && draft.moneyDown === null) {
      mode = MoneyDownMode.Percentage;
    }

    return mode;
  }

  /** Copies a valid selected source for local rendering. */
  private copyValidSelectedSource(): QualifyingRuleSourceRow | null
  {
    let sourceCopy: QualifyingRuleSourceRow | null = null;
    if (this.selectedSource !== null && this.isValidSourceId(this.selectedSource.offerSetId)) {
      sourceCopy = cloneQualifyingRuleSource(this.selectedSource);
    }

    return sourceCopy;
  }

  /** Deep-copies valid matching source detail before mapping it. */
  private copyValidSourceDetail(selectedSourceId: number | null): QualifyingRuleSourceDetail | null
  {
    let detailCopy: QualifyingRuleSourceDetail | null = null;
    if (this.sourceDetail !== null && selectedSourceId !== null && this.sourceDetail.offerSetId === selectedSourceId) {
      detailCopy = this.copySourceDetail(this.sourceDetail);
    }

    return detailCopy;
  }

  /** Deep-copies the authoritative detail contract. */
  private copySourceDetail(detail: QualifyingRuleSourceDetail): QualifyingRuleSourceDetail
  {
    const detailCopy: QualifyingRuleSourceDetail = {
      offerSetId: detail.offerSetId,
      accountId: detail.accountId,
      vehicle: this.copySourceVehicle(detail.vehicle),
      offers: this.copySourceOffers(detail.offers)
    };

    return detailCopy;
  }

  /** Copies source vehicle identity without retaining mutable references. */
  private copySourceVehicle(vehicle: QualifyingRuleSourceVehicle): QualifyingRuleSourceVehicle
  {
    const vehicleCopy: QualifyingRuleSourceVehicle = {
      title: vehicle.title,
      year: vehicle.year,
      make: vehicle.make,
      model: vehicle.model,
      trim: vehicle.trim,
      vin: vehicle.vin,
      stockNumber: vehicle.stockNumber,
      maximumPrice: vehicle.maximumPrice,
      newUsed: vehicle.newUsed,
      certified: vehicle.certified,
      visibilityScope: vehicle.visibilityScope,
      updatedAt: vehicle.updatedAt
    };

    return vehicleCopy;
  }

  /** Copies every canonical nullable offer slot. */
  private copySourceOffers(offers: QualifyingRuleSourceOffers): QualifyingRuleSourceOffers
  {
    const offersCopy: QualifyingRuleSourceOffers = {
      lease: this.copyLeaseSourceOffer(offers.lease),
      alternateLease: this.copyLeaseSourceOffer(offers.alternateLease),
      finance: this.copyFinanceSourceOffer(offers.finance),
      promotionalApr: this.copyAprSourceOffer(offers.promotionalApr)
    };

    return offersCopy;
  }

  /** Copies one nullable Lease source offer. */
  private copyLeaseSourceOffer(
    offer: QualifyingRuleSourceLeaseOffer | null
  ): QualifyingRuleSourceLeaseOffer | null
  {
    let offerCopy: QualifyingRuleSourceLeaseOffer | null = null;
    if (offer !== null) {
      offerCopy = {
        paymentAmount: offer.paymentAmount,
        moneyDown: offer.moneyDown,
        percentageDown: offer.percentageDown,
        term: offer.term,
        annualMileage: offer.annualMileage,
        additionalText: offer.additionalText
      };
    }

    return offerCopy;
  }

  /** Copies one nullable Finance source offer. */
  private copyFinanceSourceOffer(
    offer: QualifyingRuleSourceFinanceOffer | null
  ): QualifyingRuleSourceFinanceOffer | null
  {
    let offerCopy: QualifyingRuleSourceFinanceOffer | null = null;
    if (offer !== null) {
      offerCopy = {
        paymentAmount: offer.paymentAmount,
        moneyDown: offer.moneyDown,
        percentageDown: offer.percentageDown,
        apr: offer.apr,
        term: offer.term,
        additionalText: offer.additionalText
      };
    }

    return offerCopy;
  }

  /** Copies one nullable Promotional APR source offer. */
  private copyAprSourceOffer(offer: QualifyingRuleSourceAprOffer | null): QualifyingRuleSourceAprOffer | null
  {
    let offerCopy: QualifyingRuleSourceAprOffer | null = null;
    if (offer !== null) {
      offerCopy = {
        apr: offer.apr,
        term: offer.term,
        additionalText: offer.additionalText
      };
    }

    return offerCopy;
  }

  /** Creates a mapper-compatible blank detail for local clearing only. */
  private createBlankSourceDetail(): QualifyingRuleSourceDetail
  {
    const detail: QualifyingRuleSourceDetail = {
      offerSetId: minimumSourceId,
      accountId: minimumSourceId,
      vehicle: {
        title: '',
        year: minimumSourceId,
        make: '',
        model: '',
        trim: null,
        vin: null,
        stockNumber: null,
        maximumPrice: null,
        newUsed: 'N',
        certified: false,
        visibilityScope: '',
        updatedAt: ''
      },
      offers: {lease: null, alternateLease: null, finance: null, promotionalApr: null}
    };

    return detail;
  }

  /** Returns a valid selected source identity or null. */
  private resolveSelectedSourceId(): number | null
  {
    let sourceId: number | null = null;
    if (this.selectedSource !== null && this.isValidSourceId(this.selectedSource.offerSetId)) {
      sourceId = this.selectedSource.offerSetId;
    }

    return sourceId;
  }

  /** Detects invalid or mismatched selected-source/detail identities. */
  private hasSourceDetailConflict(selectedSourceId: number | null): boolean
  {
    let hasConflict = false;
    if (this.sourceDetail !== null) {
      const detailIdIsValid = this.isValidSourceId(this.sourceDetail.offerSetId);
      hasConflict = !detailIdIsValid || selectedSourceId === null || this.sourceDetail.offerSetId !== selectedSourceId;
    }

    return hasConflict;
  }

  /** Resolves the controlled unavailable or conflict message. */
  private resolveInitializationMessage(hasIdentityConflict: boolean): string
  {
    let message = 'Select a valid source and load its offer detail to configure offers.';
    if (hasIdentityConflict) {
      message = 'The selected source and authoritative offer detail do not match.';
    }

    return message;
  }

  /** Validates source IDs before they are consumed locally. */
  private isValidSourceId(sourceId: number): boolean
  {
    const isValid = Number.isSafeInteger(sourceId) && sourceId >= minimumSourceId;

    return isValid;
  }

  /** Converts a nullable finite source number to raw text form state. */
  private numberToInput(value: number | string | null): string
  {
    let inputValue = blankNumericInput;
    if (value !== null) {
      inputValue = String(value);
    }

    return inputValue;
  }

  /** Reinitializes from parent state without treating emitted live edits as new input. */
  private hasInitializationInputChange(changes: SimpleChanges): boolean
  {
    const hasSourceChange = changes['selectedSource'] !== undefined
      || changes['sourceDetail'] !== undefined;
    const hasInitialDraftChange = !this.formInitialized
      && changes['existingGeneratedDraft'] !== undefined;
    const hasChange = hasSourceChange || hasInitialDraftChange;

    return hasChange;
  }
}
